import React, { useState, useRef, useEffect, useMemo } from 'react';
import { NO_PASSWORD_MANAGER } from '../../utils/no-password-manager';
import { createPortal } from 'react-dom';
import { Icons } from './Icon';
import { APP_VERSION, STAGES } from '../../config/brand';
import { isReceivablesIntelligenceDisabled } from '../../services/receivables-intelligence';
import { isIntakeModuleEnabled, isQuotePriceListEnabled } from '../../utils/feature-flags';
import { effectiveAccess, roleRank, ROLE_RANK } from '../../config/access-areas';
// UserProfileDropdown no longer rendered in the nav bar - its controls live
// in Settings > Account now. Keeping the import reference removed stops the
// dead mount from being bundled.

// ---------------------------------------------------------------------------
// Short pill labels for the Alt/Cmd shortcut overlay. These are intentionally
// terser than the full STAGES[].name so the row fits across narrow viewports.
// Keyed by stage id (1-based) to avoid drift if a stage reorders. If a stage
// is present in STAGES but missing here, we fall back to the full name.
// ---------------------------------------------------------------------------
// Derived from STAGES[].short so there is ONE place these words live. This used
// to be a hand-maintained duplicate, and it drifted exactly the way duplicates
// do: it said BUILDER for stage 4, matching the old last-word derivation in
// StagePills rather than anything a builder would recognise as the name of a
// step. Uppercased here because this overlay is uppercase; the source of truth
// stays sentence case.
const STAGE_SHORT_LABELS: Record<number, string> = Object.fromEntries(
    STAGES.map((s: any) => [s.id, String(s.short || s.name).toUpperCase()]),
);

// ---------------------------------------------------------------------------
// Phone bar visibility contract (2026-09-22). Two mechanisms hide the bar:
// the auto-hide hook below (data-autohidden) and these body classes, which
// components.css turns into display:none for the phone workspaces. Both
// routes must zero --qm-bottom-nav-reserved; isBodyHidingBottomNav is how
// the hook sees the second route.
// ---------------------------------------------------------------------------
export const BOTTOM_NAV_HIDING_BODY_CLASSES = [
    'qm-stage4-phone-workspace-open',
    'qm-phone-cockpit-open',
    // A Clients dialog on the phone is a full-screen sheet (crm-phone.css).
    'qm-crm-sheet-open',
    // A Job dialog (Split into a new quote, Revisions) on the phone is a
    // full-screen sheet with a pinned footer; the bar would cover that footer.
    'qm-job-sheet-open',
] as const;

export function isBodyHidingBottomNav(): boolean {
    if (typeof document === 'undefined' || !document.body) return false;
    return BOTTOM_NAV_HIDING_BODY_CLASSES.some((c) => document.body.classList.contains(c));
}

/** Window event a page dispatches (detail.key) to reset a hidden bar when it
 *  changes context without changing currentPage. See the contextKey prop. */
export const BOTTOM_NAV_CONTEXT_EVENT = 'qm:bottom-nav-context';

interface BottomNavBarProps {
    currentPage?: any;
    onNavigate?: any;
    user?: any;
    onLogout?: any;
    onOpenSettings?: any;
    stage?: any;
    apiKey?: any;
    aiAssistantState?: any;
    onAiAssistantToggle?: any;
    hasNewRationale?: any;
    onRationaleViewed?: any;
    projectTitle?: string;
    /** v5.50.6: true when the loaded project is an imported historical quote. */
    isHistoryProject?: boolean;
    projectAddress?: string;
    onProjectTitleChange?: any;
    onRegenerateTitle?: any;
    onStageNavigate?: any;
    stages?: any;
    dashboardPulse?: any;
    quotesFilter?: any;
    onQuotesFilterChange?: any;
    onOpenFileExplorer?: any;
    isFileExplorerOpen?: boolean;
    /**
     * Phone auto-hide context (2026-09-22, field hardening). A page that
     * changes what it shows WITHOUT changing `currentPage` (Site Visits
     * opening one assigned job, for instance) passes the thing it opened
     * here; any change brings a hidden bar back. Default undefined = the
     * page has no sub-context and only navigation resets the bar. Pages
     * that cannot reach this prop dispatch `qm:bottom-nav-context` on
     * window with `detail.key` instead - same effect.
     */
    contextKey?: string | number | null;
    /** The server lets this account read the Quote Brain report (slice LIVE). */
    quoteBrainReportVisible?: boolean;
}

export default function BottomNavBar({
    currentPage, onNavigate, user, onLogout, onOpenSettings, stage, apiKey,
    aiAssistantState, onAiAssistantToggle, hasNewRationale, onRationaleViewed,
    // Phase 9: project title + address surfaced here (moved from top nav bar)
    projectTitle = '', projectAddress = '',
    isHistoryProject = false,
    onProjectTitleChange, onRegenerateTitle,
    // Phase 10: stage-level keyboard navigation. When supplied, Alt/Cmd+1..5
    // jumps to that stage on the QuoteMaker page. Pills are only rendered
    // when this handler is available - there's no point advertising a
    // shortcut that can't act.
    onStageNavigate,
    stages: stagesProp,
    // Dashboard pulse: { activeToday, winRate, winRateDelta } - only non-null
    // when currentPage === 'dashboard'. Rendered as a tiny info group in the
    // bar between the page label and the AI Assistant, freeing up vertical
    // real estate inside the dashboard frame itself. Peter 2026-04-20.
    dashboardPulse = null,
    // Project-history filter: { value, counts } - only non-null when the user
    // is on the Projects/AllQuotesPage. Renders compact All/Won/Pipeline/Lost
    // chips in the bar. Peter 2026-04-20.
    quotesFilter = null,
    onQuotesFilterChange,
    onOpenFileExplorer,
    // The menu's IMPORT PROJECTS action uses this to expose its active state.
    isFileExplorerOpen = false,
    contextKey = undefined,
    quoteBrainReportVisible = false,
}: BottomNavBarProps) {
    const [brandPressed, setBrandPressed] = useState(false);
    const [brandHovered, setBrandHovered] = useState(false);
    const [assistantHovered, setAssistantHovered] = useState(false);
    const [startMenuOpen, setStartMenuOpen] = useState(false);
    // Active rail row fill: a short accent wash that fades out across the row
    // rather than a flat block, so the row reads as lit from its accent edge.
    // Declared once because the mouse-up handler has to restore it exactly.
    const ACTIVE_ROW_FILL = 'linear-gradient(90deg, var(--qm-accent-a20) 0%, var(--qm-accent-a05) 55%, transparent 100%)';
    // Rail hint strip: the id of the row under the pointer or keyboard focus.
    // Null falls back to the ACTIVE row's description, so the strip is never
    // blank and never shifts layout — it has a fixed height either way.
    const [railHintId, setRailHintId] = useState<string | null>(null);
    // Focus lifecycle for the rail. Until this existed the ArrowUp/Down/Home/End
    // handler on the nav was unreachable dead code: the rail portals to the end
    // of <body>, so its controls sit after the whole application in tab order,
    // the MENU button kept focus on open, and nothing ever called .focus() on a
    // row. Escape also dropped the user on <body>.
    // (railRef itself is declared with the click-outside refs further down.)
    const railReturnFocusRef = useRef<HTMLElement | null>(null);
    const railNavigatedRef = useRef(false);
    const [showHints, setShowHints] = useState(false);

    // Is the Jobs module on for this business? It gates the IMPORT QUOTE row
    // (GOAL.md A4). Asked once per signed-in person and kept here; the row
    // stays hidden while the answer is unknown or no, and rendering never
    // waits for it. Only asked with a session token, so a signed-out bar
    // never makes a request that can only be refused.
    const quoteJobsUserKey = user
        ? String(user.id ?? user.userId ?? user.email ?? user.name ?? 'signed-in')
        : null;
    const [quoteJobsEnabled, setQuoteJobsEnabled] = useState<boolean | null>(null);
    // Bumped to ask again. A failed answer (a network drop at boot) or a "no"
    // is asked again the next time the menu opens, so the row appears without
    // a reload once the module is switched on or the network is back. A "yes"
    // is never re-asked: the server refuses the import itself if it changes.
    const [quoteJobsAsk, setQuoteJobsAsk] = useState(0);
    const quoteJobsKnownRef = useRef<boolean | null>(null);
    useEffect(() => {
        quoteJobsKnownRef.current = null;
        setQuoteJobsEnabled(null);
    }, [quoteJobsUserKey]);
    useEffect(() => {
        let cancelled = false;
        if (!quoteJobsUserKey || typeof window === 'undefined') return undefined;
        let hasToken = false;
        try {
            hasToken = Boolean(window.sessionStorage?.getItem('qm_access_token'));
        } catch {
            hasToken = false;
        }
        if (!hasToken) return undefined;
        import('../../services/quote-jobs/quote-jobs-client')
            .then(({ getQuoteJobsCapabilities }) => getQuoteJobsCapabilities())
            .then((caps) => {
                if (cancelled) return;
                quoteJobsKnownRef.current = caps?.enabled === true;
                setQuoteJobsEnabled(caps?.enabled === true);
            })
            // Unknown, not "no": the row stays hidden and the next menu open asks again.
            .catch(() => { if (!cancelled) quoteJobsKnownRef.current = false; });
        return () => { cancelled = true; };
    }, [quoteJobsUserKey, quoteJobsAsk]);
    useEffect(() => {
        if (startMenuOpen && quoteJobsKnownRef.current === false) {
            quoteJobsKnownRef.current = null;
            setQuoteJobsAsk((count) => count + 1);
        }
    }, [startMenuOpen]);

    // v5.36.14 — listen for /intake's attention broadcasts so the INTAKE rail
    // item can show its count when the operator is on another page. The
    // event fires from src/pages/IntakePage.jsx on every loadInbox change.
    // Persist in sessionStorage so the badge survives nav between pages
    // without /intake re-mounting + re-dispatching.
    const [intakeAttention, setIntakeAttention] = useState(() => {
        if (typeof window === 'undefined') return { total: 0, urgent: 0, soft: 0 };
        try {
            const raw = window.sessionStorage?.getItem('qm_intake_attention');
            return raw ? JSON.parse(raw) : { total: 0, urgent: 0, soft: 0 };
        } catch { return { total: 0, urgent: 0, soft: 0 }; }
    });

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const handler = (e: any) => {
            const d = e?.detail || {};
            const next = {
                total: Number(d.total || 0),
                urgent: Number(d.urgent || 0),
                soft: Number(d.soft || 0),
            };
            setIntakeAttention(next);
            try { window.sessionStorage?.setItem('qm_intake_attention', JSON.stringify(next)); } catch {/* quota */}
        };
        window.addEventListener('qm:intake-attention', handler);
        return () => window.removeEventListener('qm:intake-attention', handler);
    }, []);

    // v5.40.2 (UX audit Wave-2 #2): re-render the nav rail when a module
    // flag flips. Pre-fix the `hidden` checks at line ~123 / ~126 read
    // localStorage inline at render time but NOTHING triggered a re-render
    // when Settings → Modules toggled. The user had to manually F5 to see
    // the INTAKE / RECEIVABLES entry appear or disappear. Now: this
    // component listens for both the cross-tab `storage` event AND a
    // same-tab `qm:modules-changed` custom event dispatched by
    // ModulesTab.toggle — bumping a `moduleVersion` counter forces the
    // re-derive of NAV_ITEMS without a page reload.
    const [moduleVersion, setModuleVersion] = useState(0);
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const bump = () => setModuleVersion((v) => v + 1);
        const onStorage = (ev: StorageEvent) => {
            if (!ev || !ev.key) return;
            if (ev.key.startsWith('qm_use_') || ev.key.startsWith('qm_disable_')) bump();
        };
        window.addEventListener('storage', onStorage);
        window.addEventListener('qm:modules-changed', bump);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.removeEventListener('qm:modules-changed', bump);
        };
    }, []);
    // Reference moduleVersion so the linter doesn't drop it as unused. The
    // bump is what matters; the value itself is the cache-buster.
    void moduleVersion;

    const startMenuRef = useRef<HTMLDivElement | null>(null);
    // v5.34.0 (Menu v2 — Trade-Stamp Command Rail): the rail is now portalled
    // to document.body and lives OUTSIDE startMenuRef's DOM subtree. We add a
    // dedicated railRef so the click-outside handler can check both regions
    // before closing — otherwise clicking inside the rail itself collapses it.
    const railRef = useRef<HTMLElement | null>(null);

    // Move focus INTO the rail when it opens, and put it back when it closes
    // without navigating. On phones the menu is a true modal surface, so the
    // app root is inert and body scrolling is locked until the rail closes.
    useEffect(() => {
        if (typeof document === 'undefined') return;
        if (startMenuOpen) {
            railNavigatedRef.current = false;
            railReturnFocusRef.current = document.activeElement as HTMLElement | null;
            const mobileQuery = typeof window !== 'undefined'
                ? window.matchMedia('(max-width: 767px)')
                : null;
            const appRoot = document.getElementById('root');
            const previousBodyOverflow = document.body.style.overflow;
            const rootWasInert = appRoot?.hasAttribute('inert') === true;
            let isolationApplied = false;

            const restoreMobileIsolation = () => {
                if (!isolationApplied) return;
                document.body.style.overflow = previousBodyOverflow;
                if (appRoot && !rootWasInert) appRoot.removeAttribute('inert');
                isolationApplied = false;
            };
            const syncMobileIsolation = () => {
                if (!mobileQuery?.matches) {
                    restoreMobileIsolation();
                    return;
                }
                if (isolationApplied) return;
                appRoot?.setAttribute('inert', '');
                document.body.style.overflow = 'hidden';
                isolationApplied = true;
            };
            syncMobileIsolation();
            mobileQuery?.addEventListener?.('change', syncMobileIsolation);

            // Land on the current page's row so arrows start from "you are here".
            // Done synchronously: refs are attached before effects run, so the
            // element is already there. An earlier draft deferred this to
            // requestAnimationFrame, which silently never fired whenever the page
            // was not compositing (a backgrounded or headless-driven tab), leaving
            // the keyboard path dead in exactly the automated checks meant to prove it.
            const rail = railRef.current;
            const target = rail?.querySelector<HTMLElement>('.qm-rail-row[data-active="true"]')
                || rail?.querySelector<HTMLElement>('.qm-rail-row');
            target?.focus();
            return () => {
                mobileQuery?.removeEventListener?.('change', syncMobileIsolation);
                restoreMobileIsolation();
            };
        }
        // Closed. Restore only if the user backed out; if they picked a
        // destination, focus belongs to the page they asked for.
        if (!railNavigatedRef.current) {
            const prev = railReturnFocusRef.current;
            if (prev && prev.isConnected) prev.focus();
        }
        railReturnFocusRef.current = null;
        setRailHintId(null);
        return undefined;
    }, [startMenuOpen]);
    const titleInputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return undefined;
        const input = titleInputRef.current;
        if (!input || currentPage !== 'quotemaker') return undefined;

        const mobileQuery = window.matchMedia('(max-width: 640px)');
        let delayId: any = null;
        let intervalId: any = null;
        let direction = 1;

        const clearTimers = () => {
            if (delayId) window.clearTimeout(delayId);
            if (intervalId) window.clearInterval(intervalId);
            delayId = null;
            intervalId = null;
        };

        const restartAutoPan = () => {
            clearTimers();
            if (document.activeElement === input) return;
            if (!mobileQuery.matches) {
                input.scrollLeft = 0;
                return;
            }
            const maxScroll = Math.max(0, input.scrollWidth - input.clientWidth);
            if (maxScroll <= 2) {
                input.scrollLeft = 0;
                return;
            }
            input.scrollLeft = 0;
            direction = 1;
            delayId = window.setTimeout(() => {
                intervalId = window.setInterval(() => {
                    if (document.activeElement === input) return;
                    const latestMax = Math.max(0, input.scrollWidth - input.clientWidth);
                    if (latestMax <= 2) {
                        input.scrollLeft = 0;
                        return;
                    }
                    if (input.scrollLeft >= latestMax) direction = -1;
                    if (input.scrollLeft <= 0) direction = 1;
                    input.scrollLeft = Math.max(0, Math.min(latestMax, input.scrollLeft + direction));
                }, 45);
            }, 900);
        };

        const pauseForEdit = () => clearTimers();
        const resumeAfterEdit = () => restartAutoPan();

        input.addEventListener('focus', pauseForEdit);
        input.addEventListener('blur', resumeAfterEdit);
        window.addEventListener('resize', restartAutoPan);
        if (typeof mobileQuery.addEventListener === 'function') {
            mobileQuery.addEventListener('change', restartAutoPan);
        } else if (typeof mobileQuery.addListener === 'function') {
            mobileQuery.addListener(restartAutoPan);
        }

        restartAutoPan();

        return () => {
            clearTimers();
            input.removeEventListener('focus', pauseForEdit);
            input.removeEventListener('blur', resumeAfterEdit);
            window.removeEventListener('resize', restartAutoPan);
            if (typeof mobileQuery.removeEventListener === 'function') {
                mobileQuery.removeEventListener('change', restartAutoPan);
            } else if (typeof mobileQuery.removeListener === 'function') {
                mobileQuery.removeListener(restartAutoPan);
            }
        };
    }, [currentPage, projectTitle]);

    // Detect Mac so the key hint shows ⌘ rather than ⌥. Guarded for SSR/jsdom.
    const isMac = useMemo(() => {
        if (typeof navigator === 'undefined') return false;
        return /Mac|iPhone|iPad|iPod/i.test(navigator.platform || navigator.userAgent || '');
    }, []);
    const modSymbol = isMac ? '\u2318' : '\u2325'; // ⌘ or ⌥
    const modLabel = isMac ? 'Cmd' : 'Alt';

    // Resolve the set of stages we'll advertise. Only 1-5 are keyboard-addressable
    // (digit keys 1-5); if the caller supplies fewer, we render fewer pills.
    const stagesForHints = useMemo(() => {
        const src = Array.isArray(stagesProp) && stagesProp.length > 0 ? stagesProp : STAGES;
        return src
            .filter(s => typeof s?.id === 'number' && s.id >= 1 && s.id <= 5)
            .slice(0, 5);
    }, [stagesProp]);

    // short: inline descriptor that fits on the same row as the label (Windows-11
    //        style). description: full tooltip on hover. Kept short enough to
    //        render inside a ~360px menu at 11px without wrapping.
    const NAV_ITEMS = [
        // ── MAIN (slice 0-2) ──
        { id: 'dashboard',   name: 'RUN SHEET',    icon: 'QMDashboard', short: 'Every project, one sheet',     description: 'The dispatch surface: lanes, search, ages and actions across the whole quote book' },
        // QUICK EDIT is mounted as of the quote-quick-edit wave. Opening a RECOVERED
        // quote goes through editor.openRecovered, which drives `scope` FROM the
        // recovered quote instead of merging the loaded project's live scope into it,
        // and App.jsx clears the working context first so the open job cannot be
        // overwritten by the recovered one.
        { id: 'quick-edit',  name: 'QUICK EDIT',    icon: 'QMQuotes',    short: 'Fix a quote you have sent',    description: 'Reopen a quote you already produced and change a line or a price, without rerunning the pipeline' },
        // IMPORT QUOTE (GOAL.md C1): an existing quote document becomes a Job.
        // Gated like QUICK EDIT (its access area, via accessArea) and ALSO on
        // the Jobs module for this business: hidden until the capability
        // answer says yes. Not a new access area, so the backend mirror in
        // access-areas stays unchanged.
        { id: 'import-quote', name: 'IMPORT QUOTE', icon: 'Upload',     accessArea: 'quick-edit', short: 'Turn a PDF, Word or RTF quote into a Job', description: 'Drop an existing quote document from any builder, check what was read, and create an editable quote that keeps its Quote #', hidden: quoteJobsEnabled !== true },
        { id: 'quotemaker',  name: 'QUOTEMAKER',   icon: 'QMQuotes',    short: 'Create a new AI quote',        description: 'Create AI-powered professional quotes' },
        // Project intake belongs in the application menu, not as an
        // unexplained folder glyph at the far-right edge of the taskbar.
        // This is an action (opens the importer), not a route.
        { id: 'project-imports', name: 'IMPORT PROJECTS', icon: 'FolderOpen', short: 'Queue RFQ + site files', description: 'Select project folders, combine RFQ and site documentation, then prepare drafts in one queue', action: 'open-project-imports', hidden: typeof onOpenFileExplorer !== 'function' },
        // Intake — RFQ triage workbench. Visible only when the per-instance
        // localStorage flag `qm_use_intake_module` is true (v5.36.0 / session 2).
        { id: 'intake',      name: 'INTAKE',       icon: 'QMRisk',      short: 'RFQ Intake',                   description: 'Triage incoming RFQs, dispatch site visits, prepare quotes', hidden: !isIntakeModuleEnabled() },
        // Site Visits has its own tenant module and area decision. Do not hide
        // an assessor's only working surface behind the office Intake device
        // preference; effectiveAccess below remains the display gate and the
        // backend independently enforces live Site Visits access.
        { id: 'site-visits', name: 'SITE VISITS',  icon: 'QMScope',    short: 'My site visits',               description: 'Capture photos, videos and notes on a site visit, then confirm it' },
        // ── BUSINESS ──
        { id: 'analysis',          name: 'ANALYSIS',     icon: 'BarChart',    short: 'Business intelligence',        description: 'Intake volume, trade mix, pipeline ageing, clients, and outcomes as they accrue' },
        { id: 'clients',           name: 'CLIENTS',      icon: 'QMClients',   short: 'Firms and buildings',      description: 'The firms that send quote requests, and the buildings they manage' },
        { id: 'receivables',       name: 'RECEIVABLES',  icon: 'DollarSign',  short: 'Invoice recovery cases',   description: 'Review unpaid invoice context before recovery contact', hidden: isReceivablesIntelligenceDisabled() },
        { id: 'business-training', name: 'PRICING',      icon: 'QMCosting',   short: 'Rates & pricing method',   description: 'Your rates, supplier prices and pricing method' },
        // Quote Price List — Rodney's charge-out rate schedule (Pricing Engine workstream, Slice 1).
        // Visible only when the per-device flag `qm_use_price_lists` is true (default off).
        // icon 'Tag' (2026-07-03): was 'QMCosting', identical to the adjacent
        // PRICING entry — the confusable-icon class the v5.40.4 fix removed.
        { id: 'quote-price-list',  name: 'QUOTE PRICES', icon: 'Tag',         short: 'Charge-out rate schedule', description: 'The charge-out Quote Price List - the source of every quote dollar', hidden: !isQuotePriceListEnabled() },
        { id: 'aimodels',          name: 'AI MODELS',    icon: 'QMAssistant', short: 'Model assignments',        description: 'Configure which AI model handles each task in QuoteMaker' },
        // ── ADMIN (slice 8+, adminOnly) ──
        { id: 'users',             name: 'PEOPLE',            icon: 'QMClients',   adminOnly: true, short: 'Who can use QuoteMaker', description: 'Invite people, decide what each of them can reach, and reset passwords' },
        { id: 'audit',             name: 'ACTIVITY LOGS',     icon: 'QMRisk',      adminOnly: true, short: 'User audit trail',     description: 'User activity log and change tracking' },
        { id: 'developer',         name: 'DEVELOPER',         icon: 'QMScope',     adminOnly: true, short: 'Pipelines & prompts',  description: 'Pipeline management, prompt editing, live monitoring' },
        { id: 'component-library', name: 'COMPONENT LIBRARY', icon: 'QMDashboard', adminOnly: true, short: 'Design system ref',    description: 'Live reference of every .qm-* class and design token' },
        // ── PLATFORM (operator only) ──
        // Does NOT count against the rail's row budget for any customer: the
        // 'estate' area is platformOnly in access-areas, so this row resolves
        // away for every tenant role including owner. Only the QuoteMaker
        // operator ever renders a 16th row.
        { id: 'estate',            name: 'CONTROL PANEL',     icon: 'Building',    adminOnly: true, short: 'Every business, and its switches', description: 'Every business using QuoteMaker: turn their modules on or off, and suspend or restore an account' },
        // One account's page, not a role's (slice LIVE): the server answers
        // 404 to everyone else, and App passes that answer in. Hidden until
        // the server says yes.
        { id: 'quote-brain-report', name: 'QUOTE BRAIN REPORT', icon: 'BarChart', adminOnly: true, short: 'The brain, measured live', description: 'Every Quote Brain job: runs, failures, time, cost, and the draft against what was sent', hidden: quoteBrainReportVisible !== true },
        // ── PREFERENCES & PROFILE (Settings page) ──
        // Reached via rail footer button onOpenSettings or header links.
        // Marked hidden so it does not add an unneeded rail row, but resolves
        // currentNavItem, contextStrip, and page indicators when currentPage === 'settings'.
        { id: 'settings',          name: 'SETTINGS',          icon: 'Settings',    short: 'Preferences & profile', description: 'Business identity, documents, branding, and defaults', hidden: true },
    ];
    // ── Visibility is the access model, not a role string equality ──────────
    // This was `(!item.adminOnly || user?.role === 'admin')`, which had two
    // faults. It was a STRICT match on one role, so an `owner` — and, once the
    // People screen existed, a `platform_admin` — saw FEWER destinations than a
    // plain admin, i.e. seniority removed capability. And it only understood
    // two tiers, so there was no way to give one person the pricing screens
    // without also giving them the developer tools.
    //
    // Now every row is gated on the resolved area map from
    // src/config/access-areas.ts: the role's default, then the per-user
    // override the People screen writes. `user.access` is the server's own
    // answer (GET /api/auth/me); the local computation is the first-paint
    // fallback before that lands. The backend re-checks regardless, so this is
    // about showing the right menu, not about enforcement.
    const access = effectiveAccess(user);
    // accessArea lets a row share another row's area (IMPORT QUOTE uses QUICK EDIT's).
    const visibleItems = NAV_ITEMS.filter(item => !item.hidden && access[item.accessArea ?? item.id] !== false);

    // A per-destination access key (press the letter, go there) with a key cap on
    // every row shipped in v5.47.14 and was removed the same day at the operator's
    // request. Do not reinstate without asking: single-letter shortcuts that fire
    // on a bare keypress are easy to hit by accident and turn a menu into a
    // trapdoor. Arrow/Home/End focus movement is a separate thing and stays.

    // Grouping is by WHEN the builder needs it, not by what the code calls it.
    // MAIN is the daily run of work; INTAKE belongs here because triaging an RFQ
    // is the front of that same run, not a back-office task. SYSTEM gathers the
    // things you configure once and rarely revisit, which is why AI MODELS
    // sits with the admin tools rather than beside CLIENTS. Site Visits
    // is daily field work, while Analysis is a business intelligence surface.
    const mainNavIds = new Set(['dashboard', 'quick-edit', 'import-quote', 'quotemaker', 'project-imports', 'intake', 'site-visits']);
    const systemNavIds = new Set(['aimodels', 'settings']);
    const mainNavItems = visibleItems.filter(item => mainNavIds.has(item.id));
    const businessNavItems = visibleItems.filter(item => !mainNavIds.has(item.id) && !systemNavIds.has(item.id) && !item.adminOnly);
    const adminNavItems = visibleItems.filter(item => systemNavIds.has(item.id) || item.adminOnly);
    const currentNavItem = NAV_ITEMS.find(item => item.id === currentPage);
    const currentPageName = currentNavItem?.name || 'QUOTEMAKER';
    // Mirrors the rail's own grouping so the bottom bar's section label never
    // disagrees with the group the row was found under.
    const currentPageSection = currentNavItem && mainNavIds.has(currentNavItem.id)
        ? 'Main'
        : currentNavItem && (systemNavIds.has(currentNavItem.id) || currentNavItem.adminOnly)
            ? 'System'
            : 'Business';
    const contextStrip = currentPage !== 'quotemaker' && !dashboardPulse && !quotesFilter && currentNavItem
        ? {
            section: currentPageSection,
            summary: projectTitle || currentNavItem.short || currentNavItem.description,
            detail: currentNavItem.description,
            icon: currentNavItem.icon,
        }
        : null;
    const hasCenterCluster = Boolean(dashboardPulse || quotesFilter || contextStrip);

    // Close start menu on click outside or Escape. The rail is portalled to
    // document.body so we have to check BOTH the menu-button subtree AND the
    // rail's own subtree before deciding the click was "outside". Backdrop
    // clicks are routed through the backdrop element's own onClick handler,
    // so they don't go through this path.
    useEffect(() => {
        const handleClickOutside = (e: any) => {
            const inButton = startMenuRef.current && startMenuRef.current.contains(e.target);
            const inRail = railRef.current && railRef.current.contains(e.target);
            if (!inButton && !inRail) {
                setStartMenuOpen(false);
            }
        };
        const handleEscape = (e: any) => {
            if (e.key === 'Escape') setStartMenuOpen(false);
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // ------------------------------------------------------------------
    // Alt/Cmd shortcut overlay + stage jumper
    // ------------------------------------------------------------------
    // Two behaviours share one keydown listener for efficiency:
    //   1. Toggle the hint pill row while Alt/Meta is held.
    //   2. When Alt/Meta + a digit 1-5 is pressed (with no text input focused
    //      and no `data-shortcut="disabled"` ancestor), call onStageNavigate.
    //
    // We ignore repeats on the modifier toggle (holding Alt fires keydown
    // repeatedly in some browsers) so setShowHints only fires once.
    //
    // Cleanup zeroes showHints on unmount AND on window blur - otherwise
    // Alt-Tab away would leave the pills stuck visible when focus returns.
    useEffect(() => {
        // Without a stage-nav handler, there's nothing worth showing.
        if (typeof onStageNavigate !== 'function') return undefined;

        const isEditableTarget = (el: any) => {
            if (!el) return false;
            const tag = el.tagName;
            if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
            if (el.isContentEditable) return true;
            return false;
        };
        const isInDisabledScope = (el: any) => {
            if (!el || typeof el.closest !== 'function') return false;
            return !!el.closest('[data-shortcut="disabled"]');
        };

        const onKeyDown = (e: any) => {
            // Modifier toggle - only when the user holds Alt/Meta alone. We
            // deliberately fire on every keydown where the modifier is active,
            // but guard setState with the current value to avoid thrash.
            if (e.altKey || e.metaKey) {
                setShowHints(prev => (prev ? prev : true));
            }

            // Digit shortcut: Alt+1..5 (Mac: Cmd+1..5) jumps stages.
            // Guard rails:
            //   - only on the QuoteMaker page (stages don't exist elsewhere)
            //   - not when typing into an input/textarea/contenteditable
            //   - not inside a scope flagged data-shortcut="disabled"
            //   - ignore auto-repeat so holding a key doesn't fire repeatedly
            if (e.repeat) return;
            if (!(e.altKey || e.metaKey)) return;
            if (e.ctrlKey || e.shiftKey) return; // don't hijack browser chords
            if (currentPage !== 'quotemaker') return;

            const active = (typeof document !== 'undefined') ? document.activeElement : null;
            if (isEditableTarget(active)) return;
            if (isInDisabledScope(active)) return;

            // e.key is '1'..'5'; e.code is 'Digit1'..'Digit5'. Prefer e.key
            // but fall back to code for keyboard layouts where Alt mutates
            // the key (e.g. German layouts where Alt+1 can yield '¹').
            let digit = null;
            if (/^[1-5]$/.test(e.key)) digit = Number(e.key);
            else if (/^Digit[1-5]$/.test(e.code || '')) digit = Number(e.code.slice(5));
            if (digit === null) return;

            // Only fire if that stage exists in the advertised set.
            const match = stagesForHints.find(s => s.id === digit);
            if (!match) return;

            e.preventDefault();
            try { onStageNavigate(digit); } catch { /* swallow - UI shouldn't die on nav */ }
        };

        const onKeyUp = (e: any) => {
            // When the relevant modifier is released, drop the overlay.
            // Keyup fires for the individual key, so check both explicitly.
            if (e.key === 'Alt' || e.key === 'Meta' || (!e.altKey && !e.metaKey)) {
                setShowHints(false);
            }
        };

        const onBlur = () => setShowHints(false);

        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('keyup', onKeyUp);
        window.addEventListener('blur', onBlur);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('keyup', onKeyUp);
            window.removeEventListener('blur', onBlur);
        };
    }, [onStageNavigate, currentPage, stagesForHints]);

    const hintsVisible = showHints && typeof onStageNavigate === 'function'
        && currentPage === 'quotemaker' && stagesForHints.length > 0;

    // ── Mobile auto-hide (2026-08-14, MOBILE-GOAL-PROMPT.md; rules revised
    //    2026-09-22 after a real site inspection on an Android phone) ─────
    // On phone-width screens the permanent 56px bar is the single biggest
    // chrome cost. Hide on a sustained scroll-down; return on any upward
    // scroll of 8px or more, or near the top. App pages scroll in per-page
    // containers (rs-sheet__body, quote-editor-scroll, the Site Visits
    // column…), not the window, and scroll events don't bubble — but they DO
    // capture, so one capture-phase document listener covers every scroller
    // without per-page wiring. Desktop (>767px) never hides. The CSS collapse
    // rule lives in components.css (.qm-bottom-nav[data-autohidden="true"]).
    //
    // Field findings that set the current rules (2026-09-22):
    //  - There is NO bottom-reveal. The old rule re-showed the bar within
    //    24px of a scroller's end, which is exactly where a visit's Submit
    //    controls sit: the bar re-appeared under the operator's thumb as
    //    they reached them. Reaching the end of content is not a reason to
    //    show chrome.
    //  - A hidden bar must never latch. The old hook only listened for
    //    scroll events once hidden; when the scroller stopped overflowing
    //    (a visit's phase changed, the keyboard closed) no scroll event could
    //    fire and the bar stayed gone until the page changed. Now a
    //    ResizeObserver on the tracked scroller and its children rechecks
    //    overflow, and a scroll on a non-overflowing scroller un-hides.
    //  - `contextKey` (prop, or the `qm:bottom-nav-context` window event)
    //    resets the bar when a page changes what it shows without changing
    //    `currentPage` - Site Visits opening one job, for instance.
    const [autoHidden, setAutoHidden] = useState(false);
    const autoHiddenRef = useRef(autoHidden);
    autoHiddenRef.current = autoHidden;
    useEffect(() => {
        if (typeof window === 'undefined' || typeof document === 'undefined') return;
        const mq = window.matchMedia('(max-width: 767px)');
        const lastTops = new WeakMap<Element, number>();
        let downRun = 0;
        let tracked: Element | null = null;
        const overflows = (el: Element) => el.scrollHeight > el.clientHeight;
        // Un-hide when the tracked scroller can no longer scroll: hiding has
        // nothing left to reveal, and no scroll event will ever arrive.
        const recheckOverflow = () => {
            if (!tracked || !autoHiddenRef.current) return;
            if (!overflows(tracked)) { downRun = 0; setAutoHidden(false); }
        };
        const ro = typeof ResizeObserver === 'function' ? new ResizeObserver(recheckOverflow) : null;
        const observeContent = () => {
            if (!ro) return;
            ro.disconnect();
            if (!tracked) return;
            ro.observe(tracked);
            // The scroller's own box rarely changes (height: 100%); its CONTENT
            // does. Observing the direct children catches the content shrink.
            Array.from(tracked.children).forEach((child) => ro.observe(child));
        };
        // React replaces the children on a phase change; re-observe the new
        // set, and recheck at once because the swap itself may have removed
        // the overflow.
        const mo = typeof MutationObserver === 'function'
            ? new MutationObserver(() => { observeContent(); recheckOverflow(); })
            : null;
        const track = (t: Element) => {
            if (t === tracked) return;
            // downRun is per-GESTURE: interleaved small scrolls in two
            // different containers must not sum toward the hide threshold
            // (audit 2026-08-14).
            tracked = t;
            downRun = 0;
            mo?.disconnect();
            mo?.observe(t, { childList: true });
            observeContent();
        };
        const onScroll = (e: Event) => {
            if (!mq.matches) { if (autoHiddenRef.current) setAutoHidden(false); return; }
            const t = e.target;
            if (!(t instanceof Element)) return;
            if (!overflows(t)) {
                // A scroller that now fits its content still fires one scroll
                // when the browser clamps scrollTop after the content shrank.
                // If it is the scroller we hid for, that is the un-hide cue.
                if (t === tracked) recheckOverflow();
                return;
            }
            track(t);
            const top = t.scrollTop;
            const last = lastTops.get(t);
            lastTops.set(t, top);
            if (last === undefined) return;
            const delta = top - last;
            if (top < 32) { downRun = 0; setAutoHidden(false); return; }
            if (delta > 0) {
                downRun += delta;
                if (downRun > 28) setAutoHidden(true);
            } else if (delta <= -8) {
                // Any deliberate upward scroll asks for the chrome back.
                // Under 8px is finger jitter at the end of a downward flick.
                downRun = 0;
                setAutoHidden(false);
            }
        };
        const onMq = () => { if (!mq.matches) setAutoHidden(false); };
        document.addEventListener('scroll', onScroll, { capture: true, passive: true });
        mq.addEventListener?.('change', onMq);
        return () => {
            document.removeEventListener('scroll', onScroll, { capture: true } as any);
            mq.removeEventListener?.('change', onMq);
            ro?.disconnect();
            mo?.disconnect();
        };
    }, []);
    // Navigation, a page-level context change, or opening the menu always
    // brings the bar back. The window event is the transport for pages that
    // cannot reach the prop (BottomNavBar is mounted by App, not by pages).
    const [eventContextKey, setEventContextKey] = useState<unknown>(undefined);
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const onContext = (e: Event) => setEventContextKey((e as CustomEvent)?.detail?.key);
        window.addEventListener(BOTTOM_NAV_CONTEXT_EVENT, onContext);
        return () => window.removeEventListener(BOTTOM_NAV_CONTEXT_EVENT, onContext);
    }, []);
    useEffect(() => { setAutoHidden(false); }, [currentPage, contextKey, eventContextKey]);
    useEffect(() => { if (startMenuOpen) setAutoHidden(false); }, [startMenuOpen]);

    // ── --qm-bottom-nav-reserved: ONE source of truth for "is the bar there"
    // Two things hide the bar: this hook's auto-hide (data-autohidden, the
    // height collapses) and the phone workspace body classes (display:none,
    // set by Stage 4's phone workspace and the cockpit overlay). Surfaces
    // anchored with `bottom: var(--qm-bottom-nav-reserved)` (menu rail,
    // Assistant, File Explorer, update notice, floating actions) must see 0
    // under BOTH, or they float a bar-height above the fold. Before 2026-09-22
    // only auto-hide zeroed the var; the body-class route left it at 56/64px.
    // The VISIBLE value stays in components.css (:root, 56px or 64px plus
    // env(safe-area-inset-bottom)) and is restored by REMOVING the inline
    // override, so the width breakpoints and the safe-area inset are declared
    // exactly once, in CSS, and this effect is the only writer of the 0.
    const [bodyHidden, setBodyHidden] = useState(() => isBodyHidingBottomNav());
    useEffect(() => {
        if (typeof document === 'undefined' || typeof MutationObserver !== 'function') return;
        const sync = () => setBodyHidden(isBodyHidingBottomNav());
        sync();
        const mo = new MutationObserver(sync);
        mo.observe(document.body, { attributes: true, attributeFilter: ['class'] });
        return () => mo.disconnect();
    }, []);
    const barHidden = autoHidden || bodyHidden;
    useEffect(() => {
        const root = document.documentElement;
        if (barHidden) root.style.setProperty('--qm-bottom-nav-reserved', '0px');
        else root.style.removeProperty('--qm-bottom-nav-reserved');
        // Braces are load-bearing: removeProperty returns the old value, and an
        // expression-bodied cleanup would hand React a string-returning
        // Destructor (tsc TS2345).
        return () => { root.style.removeProperty('--qm-bottom-nav-reserved'); };
    }, [barHidden]);

    return (
        <div className="shrink-0 qm-bottom-nav" data-autohidden={autoHidden ? 'true' : 'false'} data-layout={hasCenterCluster ? 'grid' : 'flex'} style={{
            height: '56px',
            // Three-column grid whenever a centre cluster is in the bar
            // (2026-09-23): minmax(0,1fr) auto minmax(0,1fr) = left cluster,
            // centre, right cluster. The two side columns are the SAME width
            // whatever they hold, so the centre column, and the page pill in
            // it, sits on the bar's own centre line. The flex-spacer pair it
            // replaces centred the cluster between the page indicator and the
            // AI button instead, which was off-centre by half the heavier
            // side, and by a different amount again for assessor roles, who
            // have no AI button. The QuoteMaker page keeps the flex row: it
            // has no centre cluster and the title field wants the whole middle.
            display: hasCenterCluster ? 'grid' : 'flex',
            gridTemplateColumns: hasCenterCluster ? 'minmax(0,1fr) auto minmax(0,1fr)' : undefined,
            alignItems: 'center',
            padding: '0 16px',
            gap: '0px',
            background: '#0a0a0a',
            borderTop: '2px solid var(--qm-accent)',
            boxShadow: '0 -2px 16px rgba(0,0,0,0.4)',
            position: 'relative',
            // 9900 (was 10020 until 2026-09-22). The bar is `position:
            // relative` inside the app's flex column, so the only things that
            // can overlap it are fixed overlays. Every full-screen overlay
            // (photo review 9999, intake dialogs 9999-10000, intake toasts
            // 9998, File Explorer 9999) and the Assistant (10000/10001) sits
            // above 9900, so a black 64px strip no longer paints across the
            // bottom of a dialog the operator is trying to answer. The
            // surfaces that must stay reachable from the bar are unaffected:
            // the menu rail portals to body at 10090/10100, and the failure
            // banner stack (App 10002) sits at the top edge, never over the
            // bar. Pinned by modal-stacking-baseline.test.js and
            // bottom-nav-autohide-reveal.test.tsx.
            zIndex: 9900,
        }}>
            {/* ───────────────────────────────────────────────────────────
                 Alt/Cmd keyboard-shortcut hint overlay
                 ────────────────────────────────────────────────────────
                 Floats above the 56px bar when the user holds Alt/Meta.
                 aria-live="polite" lets screen readers announce it on
                 appearance. Always mounted so the 120ms fade plays both
                 on show AND hide - pointerEvents: 'none' when invisible
                 so it never blocks clicks. */}
            {typeof onStageNavigate === 'function' && currentPage === 'quotemaker' && stagesForHints.length > 0 && (
                <div
                    className="qm-nav-kbd-hints"
                    aria-live="polite"
                    aria-hidden={!hintsVisible}
                    role="group"
                    aria-label="Keyboard shortcuts"
                    style={{
                        position: 'absolute',
                        bottom: '64px',
                        left: '50%',
                        transform: hintsVisible
                            ? 'translate(-50%, 0)'
                            : 'translate(-50%, 6px)',
                        display: 'flex',
                        gap: '8px',
                        padding: '4px',
                        opacity: hintsVisible ? 1 : 0,
                        pointerEvents: 'none',
                        transition: 'opacity 120ms ease, transform 120ms ease',
                        zIndex: 60,
                    }}
                >
                    {stagesForHints.map((s) => {
                        const fullName = s.name || `Stage ${s.id}`;
                        const shortName = STAGE_SHORT_LABELS[s.id] || fullName.toUpperCase();
                        const isActive = s.id === stage;
                        // -------------------------------------------------
                        // WCAG 2.2 AA redesign (2026-04-20):
                        // Previously used translucent rgba() backgrounds +
                        // backdrop-blur. Because the overlay sits ABOVE the
                        // nav bar (bottom: 64px) it floats over arbitrary
                        // page content - on light pages the label text went
                        // to ~1.05:1 and was unreadable. Switched to solid
                        // opaque fills so contrast is predictable regardless
                        // of what's behind. Active state also gets a filled
                        // triangle marker so selection is conveyed by shape,
                        // not colour alone (WCAG 1.4.1 Use of Color).
                        // -------------------------------------------------
                        const pillBg = isActive ? 'var(--qm-accent)' : '#1f2937';       // slate-800
                        const pillBorder = isActive ? 'oklch(from var(--qm-accent) 0.7574 calc(c * 0.8071) calc(h + 2.6))' : '#4b5563';   // slate-600
                        const labelColor = isActive ? '#1a1a1a' : '#f9fafb';   // ink on orange / near-white on slate
                        const kbdBg = isActive ? '#1a1a1a' : '#111827';        // slate-900
                        const kbdColor = isActive ? 'oklch(from var(--qm-accent) 0.9114 calc(c * 0.3136) calc(h + 7.58))' : '#f9fafb';     // warm tint / near-white
                        const kbdBorder = isActive ? 'var(--qm-accent)' : '#6b7280';    // slate-500 for non-text UI >= 3:1
                        return (
                            <span
                                key={s.id}
                                role="listitem"
                                aria-current={isActive ? 'step' : undefined}
                                aria-label={`${modLabel} plus ${s.id}: jump to ${fullName}${isActive ? ' (current stage)' : ''}`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    padding: '6px 10px',
                                    borderRadius: '8px',
                                    background: pillBg,
                                    border: `1px solid ${pillBorder}`,
                                    color: labelColor,
                                    fontSize: '11px',
                                    fontWeight: isActive ? 800 : 700,
                                    letterSpacing: '0.06em',
                                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                    boxShadow: isActive
                                        ? '0 4px 14px color-mix(in srgb, var(--qm-accent) 35%, transparent), 0 0 0 2px rgba(10,10,10,0.6)'
                                        : '0 4px 14px rgba(0,0,0,0.5)',
                                }}
                            >
                                <kbd
                                    aria-hidden="true"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '22px',
                                        height: '18px',
                                        padding: '0 5px',
                                        borderRadius: '4px',
                                        background: kbdBg,
                                        color: kbdColor,
                                        border: `1px solid ${kbdBorder}`,
                                        fontSize: '10.5px',
                                        fontWeight: 800,
                                        lineHeight: 1,
                                    }}
                                >
                                    {modSymbol}{s.id}
                                </kbd>
                                <span>{shortName}</span>
                                {isActive && (
                                    // Non-colour affordance for the current
                                    // stage - a solid triangle "you are here"
                                    // marker. Screen readers get aria-current
                                    // above; aria-hidden here avoids double
                                    // announcement.
                                    <span
                                        aria-hidden="true"
                                        style={{
                                            display: 'inline-block',
                                            width: 0,
                                            height: 0,
                                            borderLeft: '4px solid transparent',
                                            borderRight: '4px solid transparent',
                                            borderTop: '5px solid #1a1a1a',
                                            marginLeft: '1px',
                                        }}
                                    />
                                )}
                            </span>
                        );
                    })}
                </div>
            )}

            {/* LEFT CLUSTER: menu button, divider, page indicator, and the
                section eyebrow of the context strip. One flex row so it is a
                single grid item; in the flex (QuoteMaker) layout it grows so
                the title field inside the indicator can spread. */}
            <div className="qm-nav-side qm-nav-side--start">
            {/* START MENU BUTTON - generous sizing with breathing room */}
            <div className="relative qm-nav-menu-wrap" ref={startMenuRef} style={{ zIndex: 100, padding: '0 4px 0 0' }}>
                <button
                    className="qm-nav-menu-button"
                    aria-expanded={startMenuOpen}
                    aria-haspopup="dialog"
                    onClick={() => setStartMenuOpen(!startMenuOpen)}
                    onMouseDown={() => setBrandPressed(true)}
                    onMouseUp={() => setBrandPressed(false)}
                    onMouseEnter={() => setBrandHovered(true)}
                    onMouseLeave={() => { setBrandPressed(false); setBrandHovered(false); }}
                    aria-label="Application Menu"
                    style={{
                        height: '42px',
                        padding: '0 16px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
                        borderRadius: '10px',
                        border: startMenuOpen
                            ? '2px solid var(--qm-accent)'
                            : brandHovered
                                ? '2px solid color-mix(in srgb, var(--qm-accent) 50%, transparent)'
                                : '2px solid rgba(255,255,255,0.08)',
                        background: brandPressed
                            ? 'oklch(from var(--qm-accent) 0.5283 calc(c * 1.0148) calc(h - 7.92))'
                            : startMenuOpen
                                ? 'var(--qm-gradient-brand)'
                                : brandHovered
                                    ? 'linear-gradient(135deg, #333, #252525)'
                                    : 'linear-gradient(135deg, #222, #1a1a1a)',
                        boxShadow: startMenuOpen
                            ? '0 0 16px var(--qm-accent-a30), inset 0 1px 0 rgba(255,255,255,0.15)'
                            : brandHovered
                                ? '0 2px 8px var(--qm-accent-a20), inset 0 1px 0 rgba(255,255,255,0.08)'
                                : '0 1px 3px rgba(0,0,0,0.3)',
                        transform: brandPressed ? 'scale(0.95)' : brandHovered ? 'scale(1.02)' : 'scale(1)',
                        transition: 'all 0.18s ease',
                        cursor: 'pointer',
                        position: 'relative',
                    }}
                >
                    {/* v5.40.4 (UX audit Wave-5 #2): icon swapped from
                        Icons.QMDashboard → Icons.Menu. Pre-fix the MENU
                        button used the same icon as the Dashboard nav entry
                        INSIDE the menu it opens — two icons with the same
                        visual identity, different meanings. Three-bar
                        hamburger glyph reads unambiguously as "menu" and
                        leaves the QMDashboard icon unique to the actual
                        Dashboard nav item. */}
                    <Icons.Menu size={17} aria-hidden="true" style={{
                        color: startMenuOpen ? '#ffffff' : brandHovered ? '#ffffff' : 'var(--qm-accent)',
                        transition: 'color 0.15s ease',
                        strokeWidth: 2.5,
                    }} />
                    <span style={{
                        color: startMenuOpen ? '#ffffff' : brandHovered ? '#ffffff' : '#999',
                        fontSize: '12px', fontWeight: 800, letterSpacing: '0.1em',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        transition: 'color 0.15s ease',
                    }} className="qm-nav-menu-label">MENU</span>
                    {!apiKey && (
                        <span
                            role="status"
                            aria-label="API key not configured"
                            title="API key not configured - open Settings"
                            style={{
                                position: 'absolute', top: '-3px', right: '-3px',
                                width: '9px', height: '9px', borderRadius: '50%',
                                background: '#ef4444', border: '2px solid #0a0a0a',
                            }}
                        />
                    )}
                </button>

                {/* v5.34.0 \u2014 Menu v2 \u2014 TRADE-STAMP COMMAND RAIL.
                    Left rail, portalled to document.body so it
                    escapes the BottomNavBar's overflow / position contexts
                    (per feedback_popover_portal_clipping.md). Opens on MENU
                    click, closes on MENU click / backdrop click / Escape /
                    click-outside.
                    Width: 280px deliberate middle \u2014 wider than a mobile
                    drawer (240px), narrower than a workspace pane (320px).
                    Signature: the qm-rail-pulse-dot animation beside the
                    currently-active row, mirroring the v5.33.0 icons
                    focus-dot signature. */}
                {startMenuOpen && typeof document !== 'undefined' && createPortal(
                    <>
                        {/* Backdrop sits behind the rail so the user sees
                            "this is over content". Click closes on desktop,
                            where the established rail still stops above the
                            BottomNavBar. Below the 768px desktop boundary, CSS expands both
                            layers to the full viewport and the app root is inert. */}
                        <div
                            data-qm-menu-backdrop
                            style={{
                                position: 'fixed',
                                top: 0, right: 0, bottom: 'var(--qm-bottom-nav-reserved, 56px)', left: 0,
                                background: 'rgba(0,0,0,0.45)',
                                backdropFilter: 'blur(2px)',
                                WebkitBackdropFilter: 'blur(2px)',
                                zIndex: 10090,
                                animation: 'qmRailBackdropIn 200ms ease-out both',
                            }}
                            onClick={() => setStartMenuOpen(false)}
                        />
                        {/* Desktop rail geometry keeps the BottomNavBar reserve.
                            The phone media query overrides it to 100vw by the
                            measured dynamic viewport height, with a clear close
                            control and no exposed application chrome. */}
                        <aside
                            ref={railRef}
                            /* It behaves as a modal — it sits over a 45%-black backdrop
                               and Escape closes it — so it is announced as one. The inner
                               <nav> keeps the navigation landmark. */
                            role="dialog"
                            aria-modal="true"
                            aria-label="Application menu"
                            onKeyDown={(e) => {
                                // Contain Tab. Without this, Tab walks out of the rail
                                // into the application behind the backdrop, where the
                                // user cannot see what they are focusing.
                                if (e.key !== 'Tab') return;
                                const rail = railRef.current;
                                if (!rail) return;
                                const focusables = Array.from(
                                    rail.querySelectorAll<HTMLElement>('button:not([disabled])'),
                                ).filter((el) => el.offsetParent !== null);
                                const first = focusables[0];
                                const last = focusables[focusables.length - 1];
                                if (!first || !last) return;
                                if (e.shiftKey && document.activeElement === first) {
                                    e.preventDefault();
                                    last.focus();
                                } else if (!e.shiftKey && document.activeElement === last) {
                                    e.preventDefault();
                                    first.focus();
                                }
                            }}
                            data-qm-rail
                            style={{
                                position: 'fixed',
                                top: 0, bottom: 'var(--qm-bottom-nav-reserved, 56px)', left: 0,
                                width: '320px',
                                background: '#0a0a0a',
                                borderRight: '1px solid #1f1f1f',
                                boxShadow: '4px 0 32px rgba(0,0,0,0.55)',
                                zIndex: 10100,
                                display: 'flex',
                                flexDirection: 'column',
                                animation: 'qmRailSlideIn 200ms ease-out both',
                                willChange: 'transform',
                            }}
                        >
                            {/* TRADE-STAMP HEADER \u2014 uppercase wordmark + version
                                micro-stamp. Tall + tight, 84px high. The
                                wordmark is the "stamp": fw 900, letter-
                                spacing 0.22em, near-white on near-black,
                                with a hairline orange rule underneath. */}
                            {/* Head sits on the same grid as the rows: the mark
                                in a 20px slot centred on the 34px icon column,
                                wordmark and version on the 62px text column. */}
                            <div style={{
                                padding: '16px 22px 12px 32px',
                                borderBottom: '1px solid #1f1f1f',
                                flexShrink: 0,
                            }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: '8px',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
                                        <span style={{ width: '20px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
                                            <Icons.QuoteMakerMark size={16} accent="var(--qm-accent)" style={{ color: '#f4f4f4' }} />
                                        </span>
                                        <span style={{
                                            color: '#f4f4f4',
                                            fontSize: '13px',
                                            fontWeight: 900,
                                            letterSpacing: '0.22em',
                                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                            whiteSpace: 'nowrap',
                                        }}>QUOTEMAKER</span>
                                    </div>
                                    <button
                                        /* HARD RULE 5, same trap the nav rows carry the opt-out for:
                                           without it the global button{min-height:44px;padding:12px}
                                           inflates this authored 28x28 control to 44px. Four chrome
                                           buttons were missing it, which is 44px of the rail's height
                                           budget spent on padding nobody asked for. */
                                        className="qm-inline-action-btn qm-rail-close-button"
                                        onClick={() => setStartMenuOpen(false)}
                                        aria-label="Close menu"
                                        title="Close menu (Esc)"
                                        style={{
                                            width: '28px', height: '28px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: 'transparent',
                                            border: '1px solid #2a2a2a',
                                            borderRadius: '6px',
                                            color: '#9ca3af',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            transition: 'all 150ms ease-out',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#1a1a1a';
                                            e.currentTarget.style.borderColor = 'var(--qm-accent)';
                                            e.currentTarget.style.color = 'var(--qm-accent)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.borderColor = '#2a2a2a';
                                            e.currentTarget.style.color = '#9ca3af';
                                        }}
                                    >
                                        <Icons.X size={14} />
                                    </button>
                                </div>
                                <div style={{
                                    marginTop: '5px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    paddingLeft: '30px',
                                }}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: '24px',
                                        height: '2px',
                                        background: 'var(--qm-accent)',
                                        flexShrink: 0,
                                        marginLeft: '-32px',
                                    }} />
                                    <span style={{
                                        color: '#9ca3af',
                                        /* STAMP role (see the rail type scale note above
                                           the nav): every small caps-tracked label in the
                                           rail is 9px/800/0.20em. */
                                        fontSize: '9px',
                                        fontWeight: 800,
                                        letterSpacing: '0.20em',
                                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                    }}>v{APP_VERSION}</span>
                                </div>
                            </div>

                            {/* THE RAIL TYPE SCALE: five roles, and nothing else.
                                A measured census once found 10 distinct
                                size/weight/letter-spacing triples in this 320px
                                panel, which is size soup rather than a system: the
                                eye cannot infer hierarchy from ten levels. Anything
                                added to the rail must reuse one of these five.

                                  IDENTITY      13px / 900 / 0.22em   the wordmark, once
                                  LABEL         12px / 700 / 0.08em   destination names, account name
                                  LABEL-ACTIVE  12px / 800 / 0.08em   the current row, avatar initial
                                  STAMP          9px / 800 / 0.20em   MAIN/BUSINESS/SYSTEM, SETTINGS,
                                                                      hint eyebrow, version, role
                                  PROSE         11px / 400 / normal   the hint description

                                LABEL and LABEL-ACTIVE are one role with a weight
                                switch, not two: the pair IS the you-are-here signal.

                                NAV BODY \u2014 scrolls if it overflows. Sections
                                separated by trade-stamp dividers. */}
                            <nav
                                className="qm-rail-scroll"
                                aria-label="Destinations"
                                style={{
                                    flex: '1 1 auto',
                                    overflowY: 'auto',
                                    padding: '8px 10px 14px',
                                }}
                                /* Don't clear the hint while a row still holds keyboard
                                   focus: the pointer leaving is not the keyboard leaving. */
                                onMouseLeave={(e) => {
                                    if (e.currentTarget.contains(document.activeElement)) return;
                                    setRailHintId(null);
                                }}
                                onBlur={(e) => {
                                    if (!e.currentTarget.contains(e.relatedTarget as Node)) setRailHintId(null);
                                }}
                                onKeyDown={(e) => {
                                    // Arrow keys walk the rail; Home/End jump.
                                    // Focus stays visible via the global ring.
                                    if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(e.key)) return;
                                    const items = Array.from(
                                        (e.currentTarget as HTMLElement).querySelectorAll('button'),
                                    ) as HTMLButtonElement[];
                                    if (!items.length) return;
                                    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
                                    e.preventDefault();
                                    const next = e.key === 'Home' ? 0
                                        : e.key === 'End' ? items.length - 1
                                        : e.key === 'ArrowDown' ? Math.min(items.length - 1, idx + 1)
                                        : Math.max(0, idx - 1);
                                    items[next]?.focus();
                                }}
                            >
                                {(() => {
                                    const renderGroup = (label: any, items: any, isFirst: any) => (
                                        <div key={label} style={(isFirst ? null : { marginTop: '20px' }) as React.CSSProperties | undefined}>
                                            {/* Trade-stamp section divider \u2014 tiny
                                                inline label centered on a hairline
                                                rule. Reads as a precision stamp,
                                                not a generic section header. */}
                                            {/* Label inset = row-text inset (52px: 22 row pad + 20
                                                icon + 10 gap) so section stamps sit on the same
                                                vertical grid as the item names (operator: "aligns"). */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                                padding: '0 8px 0 52px',
                                                marginBottom: '9px',
                                            }}>
                                                <span style={{
                                                    fontSize: '9px',
                                                    fontWeight: 800,
                                                    letterSpacing: '0.20em',
                                                    color: '#9ca3af',
                                                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                                    flexShrink: 0,
                                                }}>{label.toUpperCase()}</span>
                                                <span style={{ flex: '1 1 auto', height: '1px', background: '#1f1f1f' }} />
                                            </div>
                                            {items.map((item: any) => {
                                                const isProjectImporter = item.action === 'open-project-imports';
                                                const isActive = isProjectImporter ? isFileExplorerOpen : currentPage === item.id;
                                                const IconComp = Icons[item.icon];
                                                // The INTAKE rail item shows how many requests /intake
                                                // has flagged, as a quiet count at the row's end.
                                                // Tooltip + aria-label carry the count and the urgent
                                                // share for screen readers.
                                                const showAttentionBadge = item.id === 'intake' && intakeAttention.total > 0;
                                                const attentionUrgent = intakeAttention.urgent > 0;
                                                const ariaSuffix = showAttentionBadge
                                                    ? ` - ${intakeAttention.total} need${intakeAttention.total === 1 ? 's' : ''} attention${attentionUrgent ? ` (${intakeAttention.urgent} urgent)` : ''}`
                                                    : '';
                                                return (
                                                    <button
                                                        key={item.id}
                                                        /* HARD RULE 5: without the opt-out the global
                                                           button{padding:12px !important} inflates every
                                                           rail row to 53px and forces the nav to scroll. */
                                                        className="qm-inline-action-btn qm-rail-row"
                                                        onClick={() => {
                                                            // Tell the close effect a destination was chosen, so focus
                                                            // follows the user forward instead of bouncing back to MENU.
                                                            railNavigatedRef.current = true;
                                                            setStartMenuOpen(false);
                                                            if (isProjectImporter) onOpenFileExplorer?.();
                                                            else onNavigate(item.id);
                                                        }}
                                                        title={(item.description || '') + ariaSuffix}
                                                        aria-label={item.name + ariaSuffix}
                                                        /* IMPORT PROJECTS opens an overlay, it does not navigate, so it
                                                           reports aria-pressed and must NOT also claim to be the current
                                                           page. data-active carries the visual state independently. */
                                                        aria-current={!isProjectImporter && isActive ? 'page' : undefined}
                                                        data-active={isActive ? 'true' : undefined}
                                                        aria-pressed={isProjectImporter ? isFileExplorerOpen : undefined}
                                                        data-qm-menu-project-imports={isProjectImporter ? '1' : undefined}
                                                        style={{
                                                            position: 'relative',
                                                            width: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '10px',
                                                            padding: '0 10px 0 22px',
                                                            height: '34px',
                                                            background: isActive ? ACTIVE_ROW_FILL : 'transparent',
                                                            border: 'none',
                                                            borderRadius: '6px',
                                                            cursor: 'pointer',
                                                            textAlign: 'left',
                                                            /* Subtle fx: the accent edge below wipes in from the
                                                               row's own centre on hover, and the fill follows.
                                                               Interaction-driven only — nothing animates at idle. */
                                                            transition: 'background 160ms ease-out',
                                                        }}
                                                        onFocus={() => setRailHintId(item.id)}
                                                        onMouseEnter={(e) => { setRailHintId(item.id); if (!isActive) e.currentTarget.style.background = '#141414'; }}
                                                        onMouseLeave={(e) => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                                                        onMouseDown={(e) => { e.currentTarget.style.background = '#1a1a1a'; }}
                                                        onMouseUp={(e) => { e.currentTarget.style.background = isActive ? ACTIVE_ROW_FILL : '#141414'; }}
                                                    >
                                                        {/* The active-row pulse dot that used to sit here was
                                                            replaced by the .qm-rail-row::before accent edge:
                                                            two accent marks on one row is noise, and the edge
                                                            does strictly more work because it also answers
                                                            hover and keyboard focus, not just active. */}
                                                        {/* Icon tile \u2014 minimal: no background
                                                            chip, the icon stands alone. Orange
                                                            when active, slate-300 at rest. */}
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '20px',
                                                            height: '20px',
                                                            flexShrink: 0,
                                                            position: 'relative',
                                                        }}>
                                                            {IconComp && (
                                                                <IconComp
                                                                    size={16}
                                                                    accent={isActive ? 'color-mix(in oklch, var(--qm-accent) 30%, #fff)' : '#6b7280'}
                                                                    style={{ color: isActive ? 'var(--qm-accent)' : '#cbd5e1' }}
                                                                />
                                                            )}
                                                        </div>
                                                        {/* ONE LINE. The per-row description used to sit
                                                            here at 10px, which put 14 dim captions on
                                                            screen at once and read as a grey wall: the
                                                            list had no hierarchy because everything spoke
                                                            equally. The description is not lost, it moved
                                                            to the hint strip below the nav, where exactly
                                                            one shows at a time for the row under the
                                                            pointer or keyboard focus. */}
                                                        <div style={{
                                                            minWidth: 0,
                                                            flex: '1 1 auto',
                                                            fontSize: '12px',
                                                            fontWeight: isActive ? 800 : 700,
                                                            letterSpacing: '0.08em',
                                                            lineHeight: 1,
                                                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                                            color: isActive ? '#f4f4f4' : '#cbd5e1',
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                        }}>{item.name}</div>
                                                        {/* How many requests need a look, as a quiet number at
                                                            the row's end. It was a red or amber bubble on the
                                                            icon, which read as an alarm (Peter, 2026-11-03: "im
                                                            not a fan of that"). The urgent count stays in the
                                                            row's title and spoken name. */}
                                                        {showAttentionBadge && (
                                                            <span
                                                                aria-hidden="true"
                                                                data-testid="rail-intake-count"
                                                                style={{
                                                                    flex: '0 0 auto',
                                                                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                                                    fontSize: '11px',
                                                                    fontWeight: 600,
                                                                    lineHeight: 1,
                                                                    letterSpacing: 0,
                                                                    fontVariantNumeric: 'tabular-nums',
                                                                    color: isActive ? '#cbd5e1' : '#9ca3af',
                                                                }}
                                                            >
                                                                {intakeAttention.total > 99 ? '99+' : intakeAttention.total}
                                                            </span>
                                                        )}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    );
                                    return (
                                        <>
                                            {renderGroup('Main', mainNavItems, true)}
                                            {renderGroup('Business', businessNavItems, false)}
                                            {adminNavItems.length > 0 && renderGroup('System', adminNavItems, false)}
                                        </>
                                    );
                                })()}
                            </nav>

                            {/* HINT STRIP \u2014 the descriptions that used to sit under
                                every row now appear here, one at a time, for whatever
                                row the pointer or keyboard focus is on. Fixed height
                                so nothing below it ever moves; falls back to the
                                active page when nothing is hovered, so it is never
                                blank. This is also what fills the 86px void that used
                                to sit between the last row and the footer. */}
                            {(() => {
                                const hinted = NAV_ITEMS.find((i) => i.id === railHintId)
                                    || NAV_ITEMS.find((i) => i.id === currentPage);
                                return (
                                    <div
                                        /* Not a live region. Each row already speaks its own
                                           name via aria-label and its description via title,
                                           so announcing this strip on every hover and arrow
                                           press made a screen reader say each destination
                                           twice. It is a visual echo for sighted users. */
                                        aria-hidden="true"
                                        style={{
                                            flexShrink: 0,
                                            height: '42px',
                                            padding: '0 22px',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            gap: '3px',
                                            borderTop: '1px solid #1a1a1a',
                                            background: 'rgba(255,255,255,0.015)',
                                        }}
                                    >
                                        {/* Eyebrow: the hinted destination's name. The
                                            access-key cap that used to sit opposite it was
                                            removed with the shortcut itself. */}
                                        <div style={{
                                            /* STAMP role. */
                                            fontSize: '9px',
                                            fontWeight: 800,
                                            letterSpacing: '0.20em',
                                            color: 'var(--qm-accent)',
                                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}>{hinted ? hinted.name : 'MENU'}</div>
                                        <div style={{
                                            fontSize: '11px',
                                            lineHeight: 1.35,
                                            color: '#9ca3af',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 1,
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                        }}>{hinted ? (hinted.short || hinted.description) : 'Point at a destination to see what it does'}</div>
                                    </div>
                                );
                            })()}

                            {/* QUICK ACTIONS strip \u2014 sits above the user
                                footer. Settings cog + (future) shortcuts.
                                Trade-stamp idiom: two icon buttons, no
                                gradients. */}
                            <div style={{
                                borderTop: '1px solid #1f1f1f',
                                padding: '10px 16px',
                                display: 'flex',
                                gap: '8px',
                                flexShrink: 0,
                            }}>
                                <button
                                    className="qm-inline-action-btn"
                                    onClick={() => { onOpenSettings?.('business'); setStartMenuOpen(false); }}
                                    aria-label="Open settings"
                                    title="Settings"
                                    style={{
                                        flex: '1 1 auto',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        height: '32px',
                                        background: 'transparent',
                                        border: '1px solid #2a2a2a',
                                        borderRadius: '6px',
                                        color: '#cbd5e1',
                                        /* Stamp scale (9px/800/0.20em) — the rail's
                                           one label size for non-row text. */
                                        fontSize: '9px',
                                        fontWeight: 800,
                                        letterSpacing: '0.20em',
                                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                        cursor: 'pointer',
                                        transition: 'all 150ms ease-out',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.borderColor = 'var(--qm-accent)';
                                        e.currentTarget.style.color = '#f4f4f4';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.borderColor = '#2a2a2a';
                                        e.currentTarget.style.color = '#cbd5e1';
                                    }}
                                >
                                    <Icons.Settings size={13} />
                                    <span>SETTINGS</span>
                                </button>
                            </div>

                            {/* USER FOOTER \u2014 avatar + name + role pill on the
                                left, logout button on the right. Separator
                                hairline above. Click avatar = open Account
                                settings (matches popover behaviour). */}
                            <div style={{
                                borderTop: '1px solid #1f1f1f',
                                background: '#050505',
                                /* 16px left inset lands the name/role text on the
                                   62px stamp grid the rail rows align to. */
                                padding: '12px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: '10px',
                                flexShrink: 0,
                            }}>
                                <button
                                    className="qm-inline-action-btn"
                                    onClick={() => { onOpenSettings?.('account'); setStartMenuOpen(false); }}
                                    title="Account settings"
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '10px',
                                        minWidth: 0,
                                        flex: '1 1 auto',
                                        padding: '4px 6px',
                                        background: 'transparent',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        textAlign: 'left',
                                        transition: 'background 150ms ease-out',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.background = '#141414'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                >
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '50%',
                                        background: 'var(--qm-gradient-brand)',
                                        boxShadow: '0 1px 3px color-mix(in srgb, var(--qm-accent) 35%, transparent)',
                                        color: '#ffffff',
                                        /* LABEL-ACTIVE role. */
                                        fontSize: '12px',
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        flexShrink: 0,
                                    }}>
                                        {(user?.name || 'U').trim().split(/\s+/).map((s: any) => s[0]).slice(0, 2).join('').toUpperCase()}
                                    </div>
                                    <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                                        <div style={{
                                            /* LABEL role. */
                                            fontSize: '12px',
                                            fontWeight: 700,
                                            letterSpacing: '0.08em',
                                            color: '#f4f4f4',
                                            lineHeight: 1.15,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>{user?.name || 'Signed out'}</div>
                                        <div style={{
                                            /* STAMP role. */
                                            fontSize: '9px',
                                            fontWeight: 800,
                                            color: '#6b7280',
                                            lineHeight: 1.2,
                                            marginTop: '2px',
                                            letterSpacing: '0.20em',
                                            fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                        }}>
                                            {/* platform_admin is the Super Admin: say so, rather than
                                                printing the raw slug at a builder. Every other known
                                                role reads fine upper-cased. */}
                                            {user?.role === 'platform_admin' || user?.role === 'superadmin'
                                                ? 'SUPER ADMIN'
                                                : user?.role ? String(user.role).replace(/_/g, ' ').toUpperCase() : 'GUEST'}
                                        </div>
                                    </div>
                                </button>
                                {onLogout && (
                                    <button
                                        className="qm-inline-action-btn qm-rail-signout-button"
                                        onClick={(e) => { e.stopPropagation(); setStartMenuOpen(false); onLogout(); }}
                                        aria-label="Sign out"
                                        title="Sign out"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            width: '30px',
                                            height: '30px',
                                            borderRadius: '6px',
                                            background: 'transparent',
                                            border: '1px solid #2a2a2a',
                                            color: '#9ca3af',
                                            cursor: 'pointer',
                                            flexShrink: 0,
                                            transition: 'all 150ms ease-out',
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.background = '#1a0a0a';
                                            e.currentTarget.style.borderColor = '#7f1d1d';
                                            e.currentTarget.style.color = '#fca5a5';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.background = 'transparent';
                                            e.currentTarget.style.borderColor = '#2a2a2a';
                                            e.currentTarget.style.color = '#9ca3af';
                                        }}
                                    >
                                        <Icons.LogOut size={14} />
                                    </button>
                                )}
                            </div>
                        </aside>
                    </>,
                    document.body
                )}
            </div>

            {/* Divider */}
            <div className="qm-nav-section-divider" style={{ width: '1px', height: '28px', margin: '0 14px', background: 'rgba(255,255,255,0.08)' }} />

            {/* Current Page Indicator - flex behaviour adapts depending on
                whether a centre cluster is in the bar:
                  • With centre (dashboard / quotes): indicator is natural width;
                    the spacer AFTER it absorbs extra space and pushes the
                    centre cluster to the centre. Prevents overlap with the
                    centre cluster at any width. Peter 2026-04-20 bug report.
                  • Without centre (quotemaker etc.): indicator flex-grows so
                    the project title input inside has room to spread. */}
            <div className="qm-nav-page-indicator" style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: hasCenterCluster ? '0 1 auto' : '1 1 auto', minWidth: 0, maxWidth: hasCenterCluster ? '34%' : 'none' }}>
                <div className="qm-nav-page-icon" style={{
                    width: '32px', height: '32px', borderRadius: '8px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    background: 'var(--qm-gradient-brand)',
                    boxShadow: '0 2px 8px var(--qm-accent-a25)',
                    flexShrink: 0,
                }}>
                    {(() => {
                        const activeItem = currentNavItem;
                        return activeItem && Icons[activeItem.icon] ? React.createElement(Icons[activeItem.icon], { size: 15, accent: 'oklch(from var(--qm-accent) 0.9114 calc(c * 0.3136) calc(h + 7.58))', style: { color: '#ffffff' } }) : null;
                    })()}
                </div>
                <span className="qm-nav-page-label" style={{
                    color: '#e5e7eb', fontSize: '12px', fontWeight: 700,
                    letterSpacing: '0.08em', textTransform: 'uppercase',
                    fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    flexShrink: 0,
                }}>{currentPageName}</span>

                {/* Phase 9: Project title (editable) + address - only on QuoteMaker.
                    Centered in the bottom nav so it reads as the page's anchor
                    identity rather than a left-flush label (Peter 2026-04-19). */}
                {currentPage === 'quotemaker' && (onProjectTitleChange || projectTitle) && (
                    <>
                        <div className="qm-nav-title-divider" style={{ width: '1px', height: '24px', background: 'rgba(255,255,255,0.12)', flexShrink: 0 }} />
                        <div className="qm-nav-title-group" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', minWidth: 0, flex: 1 }}>
                            {/* v5.50.6: imported historical quote marker. Mirrors the Run
                                Sheet's HISTORY chip so the operator can tell mid-pipeline
                                that the open job is an archive record, not live work. */}
                            {isHistoryProject && (
                                <span className="qm-nav-history-chip" title="Imported historical quote — not a live job" style={{
                                    fontSize: '9px', fontWeight: 800, letterSpacing: '0.08em',
                                    textTransform: 'uppercase', flexShrink: 0,
                                    color: '#d6d0c6', background: 'rgba(138,131,120,0.28)',
                                    border: '1px solid rgba(138,131,120,0.55)',
                                    borderRadius: '4px', padding: '2px 6px', lineHeight: 1.2,
                                }}>History</span>
                            )}
                            <span className="qm-nav-title-label" style={{
                                fontSize: '9.5px', fontWeight: 800, letterSpacing: '0.08em',
                                color: 'var(--qm-accent)', textTransform: 'uppercase', flexShrink: 0,
                            }}>Title</span>
                            <input
                                ref={titleInputRef}
                                // type="search", the nuclear option: LastPass
                                // ignored data-lpignore AND the non-credential
                                // name/id on a text input (operator re-report
                                // 2026-08-19), but it skips search boxes. The
                                // qm-plain-search class strips the native
                                // search chrome so the field looks unchanged.
                                type="search"
                                name="qm-heading-field"
                                id="qm-heading-field"
                                value={projectTitle}
                                onChange={(e) => onProjectTitleChange?.(e.target.value)}
                                placeholder="Auto-generated from address…"
                                className="bottom-nav-title-input qm-plain-search"
                                aria-label="Project title"
                                {...NO_PASSWORD_MANAGER}
                                style={{
                                    // Peter 2026-04-20: widened the title input by ~10%.
                                    // Was clamp(120px, 24vw, 280px) / min 120px.
                                    flex: '0 1 auto',
                                    width: 'clamp(132px, 26vw, 308px)',
                                    minWidth: '132px',
                                    height: '28px',
                                    padding: '0 10px',
                                    borderRadius: '6px',
                                    background: 'rgba(255,255,255,0.06)',
                                    border: '1px solid rgba(255,255,255,0.12)',
                                    color: '#fff',
                                    fontSize: '12px',
                                    fontWeight: 700,
                                    outline: 'none',
                                    transition: 'all 0.15s ease',
                                }}
                                onFocus={(e) => { e.currentTarget.style.borderColor = 'var(--qm-accent)'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                                onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }}
                            />
                            {onRegenerateTitle && (
                                <button
                                    type="button"
                                    className="qm-nav-title-regen"
                                    onClick={onRegenerateTitle}
                                    aria-label="Regenerate title from site details"
                                    title="Regenerate title from site details"
                                    style={{
                                        width: '28px', height: '28px', borderRadius: '6px',
                                        background: 'rgba(255,255,255,0.06)',
                                        border: '1px solid rgba(255,255,255,0.12)',
                                        color: 'rgba(255,255,255,0.7)',
                                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                        cursor: 'pointer', flexShrink: 0,
                                        transition: 'all 0.15s ease',
                                    }}
                                    onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--qm-accent)'; }}
                                    onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.7)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; }}
                                >
                                    <Icons.RefreshCw size={13} aria-hidden="true" />
                                </button>
                            )}
                            {projectAddress && (
                                <>
                                    <span className="qm-nav-address-separator" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', flexShrink: 0 }}>·</span>
                                    <span
                                        className="qm-nav-project-address"
                                        title={projectAddress}
                                        style={{
                                            color: 'rgba(255,255,255,0.65)',
                                            fontSize: '11.5px',
                                            fontWeight: 500,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            minWidth: 0,
                                            flex: '0 1 auto',
                                        }}
                                    >
                                        <Icons.MapPin size={11} style={{ display: 'inline', verticalAlign: '-2px', marginRight: '3px', color: 'var(--qm-accent)' }} aria-hidden="true" />
                                        {projectAddress}
                                    </span>
                                </>
                            )}
                        </div>
                    </>
                )}
            </div>

            {/* Section eyebrow ("Main" / "Business" / "System") of the context
                strip. It hugs the page pill from the left (margin-left: auto in
                components.css) so the bar still reads "Main [pill]", but it
                lives in the LEFT column: anything sharing the centre column
                with the pill shifts the pill off the bar's centre line by half
                its own width. */}
            {contextStrip && (
                <span className="qm-nav-label qm-nav-section-label" style={{ color: 'var(--qm-accent)' }}>{contextStrip.section}</span>
            )}
            </div>

            {/* ════════════════════════════════════════════════════════════════
                CENTRE CLUSTER - the middle column of the bar's three-column
                grid, so it is centred in the BAR. Not absolute positioning
                (that overlapped the page indicator at intermediate widths,
                Peter 2026-04-20 screenshot) and not flex spacers (those centred
                it between the page indicator and the AI button, which is not
                the bar's centre, and moved when the AI button was absent).
                Holds the page pill (every page but the three below), the
                Dashboard Pulse (dashboard page) or the Filter Chips (projects
                page). Responsive CSS collapses chip labels + counts at narrow
                widths so it never overflows into the right cluster.
                ════════════════════════════════════════════════════════════════ */}
            {contextStrip && (
                <div
                    className="qm-nav-center qm-nav-center--context"
                    aria-label={`${contextStrip.section} page context: ${contextStrip.summary}`}
                    title={contextStrip.detail}
                    style={{ overflow: 'hidden' }}
                >
                    {/* The page pill: the name only. The 18px glyph that sat to
                        the left of the name went on 2026-09-23 (operator: remove
                        the small icon inside the centre pill); the page's icon is
                        already the indicator on the left. The pill is the ONLY
                        child of this column: the eyebrow and the AI status pill
                        that used to flank it moved to the side columns, because
                        a sibling here pushes the pill off centre. Width ceilings
                        per breakpoint live in components.css (.qm-nav-page-pill),
                        each sized so the side columns keep room for their
                        buttons; the pill truncates before a side is squeezed. */}
                    <span
                        className="qm-nav-pulse-pill qm-nav-page-pill"
                        style={{
                            minWidth: 0,
                            background: 'rgba(255,255,255,0.045)',
                            borderColor: 'color-mix(in srgb, var(--qm-accent) 18%, transparent)',
                        }}
                    >
                        <span
                            className="qm-nav-pulse-value"
                            style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                fontSize: '12px',   // the text floor (was 11.5px)
                                fontFamily: 'inherit',
                                fontWeight: 750,
                            }}
                        >
                            {contextStrip.summary}
                        </span>
                    </span>
                </div>
            )}

            {dashboardPulse && typeof dashboardPulse.unsentCount === 'number' ? (
                /* 2026-07-27 (Run Sheet rebuild): the dashboard centre slot now
                   carries the UNSENT backlog — quotes that are finished and have
                   never gone to the client. The previous "touched in the last
                   24h" pill reported 109 of 109 on live data, because the Dexie
                   sync rewrites updated_at on every hydrate; it was measuring
                   cache activity, not work. This is the one figure worth keeping
                   in front of the operator after they leave the dashboard. */
                <div
                    className="qm-nav-center qm-nav-center--pulse"
                    aria-label={dashboardPulse.unsentCount > 0
                        ? `${dashboardPulse.unsentCount} finished quotes have not been sent, worth $${(dashboardPulse.unsentValue || 0).toLocaleString('en-AU')} ex GST. Oldest is ${dashboardPulse.oldestUnsentDays} days old.`
                        : 'Every finished quote has been sent'}
                >
                    <span className="qm-nav-label">UNSENT</span>
                    <span className="qm-nav-pulse-pill" title={`${dashboardPulse.unsentCount} finished quote${dashboardPulse.unsentCount === 1 ? '' : 's'} not yet sent to a client`}>
                        <span aria-hidden="true" className="qm-nav-pulse-dot" data-on={dashboardPulse.unsentCount > 0 ? 'true' : 'false'} />
                        <span className="qm-nav-pulse-value" style={{ fontVariantNumeric: 'tabular-nums' }}>{dashboardPulse.unsentCount}</span>
                        <span className="qm-nav-pulse-unit" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            ${(dashboardPulse.unsentValue || 0).toLocaleString('en-AU')}
                        </span>
                    </span>
                    {dashboardPulse.unsentCount > 0 && (
                        <span className="qm-nav-pulse-pill" title={`The oldest unsent quote arrived ${dashboardPulse.oldestUnsentDays} days ago`}>
                            <span className="qm-nav-pulse-eyebrow">Oldest</span>
                            <span
                                className="qm-nav-pulse-value"
                                style={{
                                    fontVariantNumeric: 'tabular-nums',
                                    color: (dashboardPulse.oldestUnsentDays || 0) > 21 ? 'var(--qm-brand-orange, var(--qm-accent))' : undefined,
                                }}
                            >
                                {dashboardPulse.oldestUnsentDays}d
                            </span>
                        </span>
                    )}
                </div>
            ) : dashboardPulse && (
                <div className="qm-nav-center qm-nav-center--pulse" aria-label={`${dashboardPulse.activeToday} projects touched in the last 24 hours, total value $${(dashboardPulse.todayValue || 0).toLocaleString('en-AU')}`}>
                    {/* Peter 2026-04-20: was three loose inline spans per metric
                        with mixed font-sizes and no container, which read as
                        "squashed + slightly misaligned". Each metric is now its
                        own pill with a consistent line-height:1 baseline and a
                        subtle dark fill. TODAY label sits to the left as an
                        orange eyebrow.
                        Peter 2026-04-21: replaced the Win-rate pill (an all-time
                        stat that didn't belong under a TODAY label) with today's
                        total quote value - sum of ex-GST scope amounts across
                        projects touched in the last 24h. */}
                    <span className="qm-nav-label">TODAY</span>
                    <span className="qm-nav-pulse-pill" title={`${dashboardPulse.activeToday} project${dashboardPulse.activeToday === 1 ? '' : 's'} touched in the last 24h`}>
                        <span aria-hidden="true" className="qm-nav-pulse-dot" data-on={dashboardPulse.activeToday > 0 ? 'true' : 'false'} />
                        <span className="qm-nav-pulse-value">{dashboardPulse.activeToday}</span>
                        <span className="qm-nav-pulse-unit">24h</span>
                    </span>
                    {/* Legacy shape (no unsent figures supplied). Kept so any
                        caller not yet updated still renders something sane. */}
                    <span className="qm-nav-pulse-pill" title={`Total quote value across projects touched in the last 24h: $${(dashboardPulse.todayValue || 0).toLocaleString('en-AU')} ex-GST`}>
                        <span className="qm-nav-pulse-eyebrow">Value</span>
                        <span
                            className="qm-nav-pulse-value"
                            style={{
                                fontVariantNumeric: 'tabular-nums',
                                color: 'var(--qm-brand-orange, var(--qm-accent))',
                            }}
                        >
                            ${(dashboardPulse.todayValue || 0).toLocaleString('en-AU')}
                        </span>
                    </span>
                </div>
            )}

            {quotesFilter && (
                <div className="qm-nav-center qm-nav-center--filter" role="group" aria-label="Filter projects by status">
                    <span className="qm-nav-label">FILTER</span>
                    {[
                        { key: 'all',     label: 'All',      color: '#f9fafb', dot: '#78716c' },
                        { key: 'won',     label: 'Won',      color: '#34d399', dot: '#10b981' },
                        { key: 'pending', label: 'Pipeline', color: '#fbbf24', dot: 'var(--qm-accent)' },
                        { key: 'lost',    label: 'Lost',     color: '#f87171', dot: '#be123c' },
                    ].map(chip => {
                        const active = quotesFilter.value === chip.key;
                        const count = quotesFilter.counts?.[chip.key] ?? 0;
                        return (
                            <button
                                key={chip.key}
                                type="button"
                                onClick={() => onQuotesFilterChange?.(chip.key)}
                                aria-pressed={active}
                                aria-label={`${chip.label}, ${count} projects`}
                                title={`Show ${chip.label.toLowerCase()} · ${count}`}
                                className={`qm-nav-filter-chip${active ? ' is-active' : ''}`}
                                style={{
                                    '--qm-chip-dot': chip.dot,
                                    '--qm-chip-color': chip.color,
                                } as React.CSSProperties}
                            >
                                <span aria-hidden="true" className="qm-nav-filter-chip-dot" />
                                <span className="qm-nav-filter-chip-label">{chip.label}</span>
                                <span className="qm-nav-filter-chip-count">{count}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* RIGHT CLUSTER: the AI status pill and the AI button, aligned to
                the end of the third grid column (justify-self: end). Its width
                never moves the centre column: the side columns are equal by
                construction, so an assessor's bar (no AI button) and an office
                bar put the page pill on the same pixel. */}
            <div className="qm-nav-side qm-nav-side--end">
            {/* AI status pill. Moved here from the centre cluster on
                2026-09-23: it reports the AI key state, which belongs beside
                the AI button, and as a centre-column sibling it pushed the
                page pill off centre. Phone rules in components.css drop its
                word and keep the dot. */}
            {contextStrip && (
                <span
                    className="qm-nav-pulse-pill qm-nav-ai-status"
                    title={apiKey ? 'AI assistant is configured' : 'Open Settings to add API keys'}
                    aria-label={apiKey ? 'AI assistant configured' : 'AI assistant keys required'}
                    style={{
                        padding: '4px 8px',
                        gap: '5px',
                        flexShrink: 0,
                    }}
                >
                    <span
                        aria-hidden="true"
                        className="qm-nav-pulse-dot"
                        data-on={apiKey ? 'true' : 'false'}
                        style={(!apiKey ? { background: '#ef4444' } : null) as React.CSSProperties | undefined}
                    />
                    <span className="qm-nav-pulse-unit">{apiKey ? 'AI Ready' : 'Keys'}</span>
                </span>
            )}

            {/* AI Assistant Button. Gated on the TOGGLE as well as the key:
                App.tsx withholds onAiAssistantToggle for site-assessor roles
                (2026-09-22), and a button whose only outcome is a no-op is
                worse than none — the assessor slice found it still drawn,
                inert, under `apiKey` alone. `apiKey` stays in the gate because
                a falsy key drives the red "Keys" pill and the MENU dot. */}
            {apiKey && onAiAssistantToggle && (
                <>
                <div className="qm-nav-section-divider qm-nav-section-divider--ai" style={{ width: '1px', height: '28px', margin: '0 10px', background: 'rgba(255,255,255,0.08)' }} />
                <button
                    className="qm-nav-ai-button"
                    onClick={() => {
                        if (onAiAssistantToggle) onAiAssistantToggle();
                        if (hasNewRationale && onRationaleViewed) onRationaleViewed();
                    }}
                    onMouseEnter={() => setAssistantHovered(true)}
                    onMouseLeave={() => setAssistantHovered(false)}
                    aria-label={aiAssistantState?.isOpen ? 'Toggle Assistant size' : 'Open Assistant'}
                    title={aiAssistantState?.isOpen ? 'Toggle Assistant size' : 'Open Assistant'}
                    style={{
                        height: '42px',
                        padding: '0 14px',
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px',
                        borderRadius: '10px', position: 'relative',
                        border: aiAssistantState?.isOpen
                            ? '2px solid rgba(255,255,255,0.3)'
                            : assistantHovered
                                ? '2px solid color-mix(in srgb, var(--qm-accent) 50%, transparent)'
                                : '2px solid rgba(255,255,255,0.08)',
                        background: aiAssistantState?.isOpen
                            ? 'var(--qm-gradient-brand)'
                            : assistantHovered
                                ? 'linear-gradient(135deg, #333, #252525)'
                                : 'linear-gradient(135deg, #222, #1a1a1a)',
                        boxShadow: aiAssistantState?.isOpen
                            ? '0 0 16px var(--qm-accent-a30), inset 0 1px 0 rgba(255,255,255,0.15)'
                            : assistantHovered
                                ? '0 2px 8px var(--qm-accent-a20)'
                                : '0 1px 3px rgba(0,0,0,0.3)',
                        cursor: 'pointer',
                        transition: 'all 0.18s ease',
                        transform: assistantHovered && !aiAssistantState?.isOpen ? 'scale(1.02)' : 'scale(1)',
                    }}
                >
                    {aiAssistantState?.isOpen ? (
                        <Icons.Minimize2 size={15} aria-hidden="true" style={{ color: '#ffffff' }} />
                    ) : (
                        <Icons.QMAssistant size={15} aria-hidden="true" accent={assistantHovered ? 'oklch(from var(--qm-accent) 0.9114 calc(c * 0.3136) calc(h + 7.58))' : 'var(--qm-accent)'} style={{ color: assistantHovered ? '#ffffff' : 'var(--qm-accent)', transition: 'color 0.15s ease' }} />
                    )}
                    <span className="qm-nav-ai-label" style={{
                        color: aiAssistantState?.isOpen ? '#ffffff' : assistantHovered ? '#ffffff' : '#999',
                        fontSize: '12px', fontWeight: 800, letterSpacing: '0.06em',
                        fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                        transition: 'color 0.15s ease',
                    }}>AI</span>
                    {aiAssistantState?.isProcessing && (
                        <span
                            role="status"
                            aria-label="Assistant processing"
                            title="Assistant processing"
                            style={{
                                position: 'absolute', top: '-3px', right: '-3px',
                                width: '10px', height: '10px', borderRadius: '50%',
                                background: 'var(--qm-accent)', border: '2px solid #0a0a0a',
                                animation: 'smoothPulse 1.5s ease-in-out infinite',
                            }}
                        />
                    )}
                    {hasNewRationale && !aiAssistantState?.isOpen && (
                        <span
                            role="status"
                            aria-label="New assistant rationale available"
                            title="New assistant rationale available"
                            style={{
                                position: 'absolute', top: '-3px', right: '-3px',
                                width: '10px', height: '10px', borderRadius: '50%',
                                background: '#10b981', border: '2px solid #0a0a0a',
                            }}
                        />
                    )}
                </button>
                <div className="qm-nav-section-divider qm-nav-section-divider--files" style={{ width: '1px', height: '28px', margin: '0 10px', background: 'rgba(255,255,255,0.08)' }} />
                </>
            )}
            </div>

            {/* Project import moved into MENU → IMPORT PROJECTS. Keeping the
                action in the labelled command rail makes it discoverable and
                removes the ambiguous bottom-right folder icon. */}

            {/* User Profile dropdown removed from nav bar (Peter 2026-04-20).
                Account info + logout now live inside Settings modal > Account
                panel - reachable via the Settings menu item in the Start menu. */}
        </div>
    );
}
